// src/features/dashboard/admin/UsersTable.jsx
import React, { useState, useMemo } from 'react';
import { RoleBadge } from './RoleBadge';

export const UsersTable = ({ users, onEdit, onDelete, roleFilter, setRoleFilter }) => {
  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(() => {
    let result = users;
    if (roleFilter !== "All") {
      result = result.filter(u => u.role?.toLowerCase() === roleFilter.toLowerCase());
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.surname.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    return result;
  }, [users, roleFilter, search]);

  const renderStatus = (status) => {
    const statusMap = {
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      partial: { label: 'Partial', className: 'bg-yellow-100 text-yellow-800' },
      inactive: { label: 'Inactive', className: 'bg-red-100 text-red-800' },
    };
    const s = status?.toLowerCase() || 'active';
    const info = statusMap[s] || statusMap.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${info.className}`}>
        {info.label}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border rounded px-3 py-1 text-sm"
          >
            <option value="All">All Roles</option>
            <option value="Student">Student</option>
            <option value="Teacher">Teacher</option>
            <option value="Admin">Admin</option>
          </select>
          <input
            type="text"
            placeholder="Search name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-1 text-sm"
          />
        </div>
        <button
          onClick={() => onEdit(null)}
          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
        >
          + Add User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Surname</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-3 text-center text-gray-500">No users found.</td></tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={`${user.userId}-${user.subject || user.role || 'no-subject'}`}>
                  <td className="px-4 py-2">{user.name}</td>
                  <td className="px-4 py-2">{user.surname}</td>
                  <td className="px-4 py-2">{user.email}</td>
                  <td className="px-4 py-2"><RoleBadge role={user.role} /></td>
                  <td className="px-4 py-2">{renderStatus(user.status)}</td>
                  <td className="px-4 py-2">{user.subject || '-'}</td>
                  <td className="px-4 py-2">{user.grade || '-'}</td>
                  <td className="px-4 py-2">{user.teacher || '-'}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => onEdit(user)} className="text-blue-600 hover:underline mr-2 text-sm">Edit</button>
                    <button onClick={() => onDelete(user)} className="text-red-600 hover:underline text-sm">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};